import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, MapPin, Aperture, Clock } from "lucide-react";
import { Reveal } from "./Reveal";

const CASES = [
  {
    id: "wtac-2026",
    title: "World Time Attack Challenge 2026",
    category: "Cinematic Documentary & Track Action",
    location: "Sydney Motorsport Park",
    stats: "1,000+ HP Aerodynamic Monsters | 4K 120fps High-Speed Tracking",
    image: "/media/wtac.jpg",
    video: "/media/wtac-aftermovie.mp4",
    videoLabel: "PLAY THE 2026 AFTERMOVIE",
    wide: "md:w-full",
  },
  {
    id: "unmarked-2026",
    title: "Unmarked 2026",
    category: "Underground Automotive Culture & Commercial Short Film",
    location: "Secret Location / Night Urban",
    stats: "Custom Anamorphic Lenses | Studio Headlamp Lighting Rigs",
    image: "/media/unmarked.jpg",
    video: "/media/unmarked-film.mp4",
    videoPoster: "/media/unmarked-poster.jpg",
    videoLabel: "PLAY THE VOL. V SYDNEY AFTERMOVIE",
    wide: "md:w-[92%] md:ml-0",
  },
];

function CaseModal({ study, onClose }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <motion.div
      data-testid="case-study-modal"
      className="fixed inset-0 z-[90] bg-ink/95 backdrop-blur-md overflow-y-auto"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <button
        data-testid="modal-close-button"
        onClick={onClose}
        aria-label="Close"
        className="fixed top-6 right-6 z-10 w-12 h-12 flex items-center justify-center border border-white/25 text-white hover:border-signal hover:text-signal transition-colors bg-ink/60"
      >
        <X size={20} />
      </button>

      <motion.div
        className="max-w-5xl mx-auto px-6 py-24"
        initial={{ y: 60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        onClick={(e) => e.stopPropagation()}
      >
        <p className="font-mono text-[10px] tracking-[0.4em] text-signal mb-4">
          CASE STUDY — {study.id.replace(/-/g, " ").toUpperCase()}
        </p>
        <h3
          className="font-display text-4xl md:text-6xl text-white mb-8"
          style={{ fontWeight: 800 }}
        >
          {study.title}
        </h3>

        <div className="letterbox overflow-hidden bg-black">
          {study.video ? (
            <video
              data-testid="case-study-video"
              src={study.video}
              poster={study.videoPoster || study.image}
              controls
              autoPlay
              muted
              loop
              playsInline
              className="w-full aspect-video object-cover"
            />
          ) : (
            <img
              src={study.image}
              alt={study.title}
              className="w-full aspect-video object-cover"
            />
          )}
        </div>

        <div className="grid md:grid-cols-3 gap-6 mt-8">
          {[
            { icon: Aperture, label: "DISCIPLINE", value: study.category },
            { icon: MapPin, label: "LOCATION", value: study.location },
            { icon: Clock, label: "PRODUCTION", value: study.stats },
          ].map(({ icon: Icon, label, value }) => (
            <div key={label} className="border-t border-white/15 pt-4">
              <p className="font-mono text-[10px] tracking-[0.3em] text-smoke flex items-center gap-2 mb-2">
                <Icon size={12} className="text-signal" /> {label}
              </p>
              <p className="text-sm text-white/85 leading-relaxed">{value}</p>
            </div>
          ))}
        </div>

        <p className="mt-10 font-mono text-[11px] tracking-[0.25em] text-smoke">
          {study.videoLabel || "FULL FILM — FOOTAGE DROPS SOON"}
        </p>
      </motion.div>
    </motion.div>
  );
}

export default function Work() {
  const [selected, setSelected] = useState(null);

  return (
    <section id="work" data-testid="work-section" className="relative bg-ink py-24 md:py-36">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <Reveal>
          <div className="flex items-end justify-between mb-14 md:mb-20">
            <div>
              <p className="font-mono text-[10px] tracking-[0.4em] text-signal mb-4">01 — SELECTED WORK</p>
              <h2
                className="font-display text-5xl md:text-7xl text-white leading-[0.95]"
                style={{ fontWeight: 900 }}
              >
                FRAMES FROM <span className="text-outline">THE EDGE</span>
              </h2>
            </div>
            <p className="hidden md:block font-mono text-[11px] tracking-[0.25em] text-smoke text-right">
              2026 SEASON COVERAGE
              <br />
              <span className="text-white/50">CLICK A FRAME TO EXPAND</span>
            </p>
          </div>
        </Reveal>

        <div className="space-y-16 md:space-y-28">
          {CASES.map((study, i) => (
            <Reveal key={study.id} delay={0.05}>
              <article
                data-testid={`case-study-card-${study.id}`}
                onClick={() => setSelected(study)}
                className={`group cursor-pointer ${study.wide}`}
              >
                <div className="relative overflow-hidden letterbox">
                  <img
                    src={study.image}
                    alt={study.title}
                    loading="lazy"
                    className="w-full aspect-[16/9] object-cover transition-transform duration-[1200ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.05]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-transparent to-transparent opacity-70 group-hover:opacity-40 transition-opacity duration-500" />
                  <div className="absolute bottom-0 left-0 h-[3px] bg-signal w-0 group-hover:w-full transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]" />
                  <span className="absolute top-5 left-5 font-mono text-[10px] tracking-[0.35em] text-white/80 bg-ink/60 backdrop-blur-sm px-3 py-1.5 border border-white/10">
                    FILM {String(i + 1).padStart(2, "0")}
                  </span>
                  {study.video && (
                    <span className="absolute inset-0 m-auto w-16 h-16 md:w-20 md:h-20 flex items-center justify-center bg-signal/90 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <span className="w-0 h-0 border-y-8 border-y-transparent border-l-[14px] border-l-white ml-1.5" />
                    </span>
                  )}
                </div>

                <div className="mt-6 flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                  <h3
                    className="font-display text-3xl md:text-5xl text-white group-hover:text-signal transition-colors duration-300"
                    style={{ fontWeight: 800 }}
                  >
                    {study.title.toUpperCase()}
                  </h3>
                  <div className="md:text-right">
                    <p className="font-mono text-[10px] tracking-[0.3em] text-smoke uppercase">
                      {study.category}
                    </p>
                    <p className="font-mono text-[10px] tracking-[0.3em] text-white/50 mt-1.5">
                      {study.stats}
                    </p>
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {selected && <CaseModal study={selected} onClose={() => setSelected(null)} />}
      </AnimatePresence>
    </section>
  );
}
